import { useEffect, useRef } from "react";

/**
 * Re-run `callback` whenever the tab regains focus (window `focus` event)
 * or the document becomes visible again (`visibilitychange` to "visible"),
 * throttled so we never call it more often than `minIntervalMs`.
 *
 * Used to wake up stale UI when the user completes a side-quest in another
 * tab — for example, finishing Stripe Connect Express onboarding — without
 * forcing them to manually refresh the page they came from.
 *
 * The throttle is shared across both event sources because browsers
 * frequently fire both within a few milliseconds when switching tabs, and
 * we don't want a single tab switch to trigger two API calls.
 *
 * Pass `enabled = false` to disable the listeners entirely (e.g. when there's
 * nothing to refresh on this page right now). Toggling `enabled` resets the
 * throttle so the next focus after re-enabling fires immediately.
 */
export function useRefreshOnFocus(
  callback: () => void | Promise<void>,
  options: { enabled?: boolean; minIntervalMs?: number } = {},
) {
  const { enabled = true, minIntervalMs = 10_000 } = options;
  const callbackRef = useRef(callback);
  const lastRunRef = useRef(0);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof document === "undefined" || typeof window === "undefined") return;

    lastRunRef.current = 0;

    const onFocusOrVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastRunRef.current < minIntervalMs) return;
      lastRunRef.current = now;
      void callbackRef.current();
    };

    document.addEventListener("visibilitychange", onFocusOrVisible);
    window.addEventListener("focus", onFocusOrVisible);
    return () => {
      document.removeEventListener("visibilitychange", onFocusOrVisible);
      window.removeEventListener("focus", onFocusOrVisible);
    };
  }, [enabled, minIntervalMs]);
}
